import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Space, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { receiptFile } from '@/api/payouts';
import { apiErrorMessage } from '@/api/client';

const { Text } = Typography;

/**
 * 转账确认函的页内预览。
 *
 * 🔴 **为什么要有这么一个东西，而不是把地址丢给浏览器**：确认函存在 COS，而那个桶
 * 开着「强制下载」—— 响应头恒为 `Content-Disposition: attachment`，签名 URL 上带
 * `response-content-disposition=inline` 也覆盖不掉（都是对真实对象实测的）。
 * 浏览器拿到那个地址只会把文件存下来，没法看。桶与 agency-crm 共用，改不得。
 * 所以字节由后端代出来，这里拿 Blob 自己渲染。
 *
 * 🔴 **只取一次字节**：预览拿到的 Blob 同时供「下载」用，另存不再回源。确认函里有
 * 银行账号，少一次传输就少一次暴露面；而且它已经在内存里了，再取一次纯属浪费。
 *
 * ⚠️ 用 `<iframe>` 套 blob: 地址，交给浏览器自带的 PDF 阅读器。没有引入 pdf.js 那类
 * 库：那会给前端包再加几百 KB，而我们要的只是「能看一眼」。万一某个浏览器渲染不了，
 * 「下载」按钮始终在那儿 —— 所以这里不存在「看不了就没辙了」的死角。
 */
export function ReceiptPreviewModal(props: {
  /** 要预览的打款记录 id；null = 关闭。 */
  payoutId: string | null;
  /** 标题里的副标题，通常是收款人与金额，让人确认自己打开的是哪一笔。 */
  subtitle?: string;
  onClose: () => void;
}) {
  const { payoutId, subtitle, onClose } = props;
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState('transfer-confirmation.pdf');
  const [error, setError] = useState<string | null>(null);
  /**
   * 初值就是 true，而不是在 effect 里 `setLoading(true)`。
   * 调用方给这个组件挂了 `key={payoutId}`，每次打开都是一次全新挂载，初值天然就对 ——
   * 在 effect 里同步 setState 会多触发一轮渲染，lint 也会因此告警。
   */
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!payoutId) return;
    let cancelled = false;
    /** 本次创建的 blob 地址，交给下面的清理函数回收。 */
    let created: string | null = null;

    receiptFile(payoutId)
      .then(({ blob, filename: name }) => {
        // 请求发出后用户就把弹窗关了：别再往一个已经卸载的界面里塞状态
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
        setFilename(name);
      })
      .catch((err) => {
        if (cancelled) return;
        // 🔴 原样显示服务端的话：它区分了「取不到，去 Airwallex 后台下载」与
        // 「钱没动，压根不存在确认函」，前端另写一句笼统的会把这个区别抹掉
        setError(apiErrorMessage(err, '转账确认函打不开，请稍后重试'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      // 🔴 blob 地址不主动还回去的话，整份 PDF 会一直挂在这一页的内存里。
      // 放在清理函数里而不是关闭按钮里：卸载、切换记录、组件被意外移除都能兜住。
      if (created) {
        const u = created;
        // 与 imports.ts 的下载同一条理由：同步 revoke 可能打断刚起动的下载，
        // 挪到下一个宏任务，给浏览器一点时间先把下载起动起来
        setTimeout(() => URL.revokeObjectURL(u), 0);
      }
    };
  }, [payoutId]);

  function download() {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  }

  return (
    <Modal
      open={payoutId !== null}
      onCancel={onClose}
      width={900}
      title={
        <Space orientation="vertical" size={0}>
          <span>转账确认函</span>
          {subtitle && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
              {subtitle}
            </Text>
          )}
        </Space>
      }
      footer={
        <Space>
          {/* 🔴 确认函证明的是「Airwallex 受理了这笔转账指令」，不是「钱到了」。
              这句话在列表里已经说过一次，预览里再说一次——人在盯着一份 PDF 的时候
              最容易把它读成「这笔打完了」。 */}
          <Text type="secondary" style={{ fontSize: 12, marginRight: 'auto' }}>
            确认函说明 Airwallex 已受理这笔转账指令，钱是否到账以「状态」列为准。
          </Text>
          <Button onClick={onClose}>关闭</Button>
          <Button type="primary" icon={<DownloadOutlined />} disabled={!url} onClick={download}>
            下载
          </Button>
        </Space>
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin description="正在取转账确认函…" />
        </div>
      )}
      {error && <Alert type="error" showIcon title="打不开这份转账确认函" description={error} />}
      {url && (
        <iframe
          title="转账确认函预览"
          src={url}
          style={{ width: '100%', height: '70vh', border: '1px solid #f0f0f0', borderRadius: 6 }}
        />
      )}
    </Modal>
  );
}
